/**
 * OIDC Token Refresh — proactively refreshes the access token before it expires.
 *
 * The OIDC provider (AIM) issues short-lived JWTs. The refresh token is stored
 * server-side, so the client only holds the access token. This module parses
 * the JWT exp claim and schedules a refresh 60 seconds before expiry via the
 * backend's /oidc/aim-refresh/ endpoint.
 *
 * Behaviour:
 * - On schedule: parses exp from the current access token JWT.
 * - Fires the refresh 60 seconds before expiry (or immediately if already due).
 * - On success: stores the new access token and reschedules.
 * - On failure: clears tokens and redirects to welcome-back.
 * - On 409 (another tab is refreshing): clears timer and waits for the new
 *   token to arrive via the cross-tab storage event.
 * - Can be paused/resumed externally (used by useOidcIdleGuard).
 * - Guards against race conditions with logout (re-checks isOidcSession after
 *   each async boundary).
 * - Stops the SM cookie refresh on cancellation (logout/SAML takeover).
 */
import { ACCESS_TOKEN } from '@pequity/shared/types/constants';
import { isOidcSession } from '@pequity/shared/utils/oidc';
import { setAccessToken } from '@pequity/shared/utils/token';
import { refreshOidcToken } from '@/api/auth';
import { stopSmCookieRefresh } from '@/composables/useSmCookieRefresh';
import { clearTokens } from '@/utils/helpers';

const REFRESH_BEFORE_EXPIRY_MS = 60_000;

let refreshTimer: ReturnType<typeof setTimeout> | null = null;
let listening = false;
let paused = false;

const clearRefreshTimer = () => {
  if (refreshTimer !== null) {
    clearTimeout(refreshTimer);
    refreshTimer = null;
  }
};

const getTokenExpMs = (token: string): number | null => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return typeof payload.exp === 'number' ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
};

const redirectToWelcomeBack = () => {
  clearRefreshTimer();
  stopSmCookieRefresh();
  clearTokens();
  window.location.href = '/v3/welcome-back/';
};

const scheduleNextRefresh = (): void => {
  const accessToken = localStorage.getItem(ACCESS_TOKEN);
  if (!accessToken) return;

  const expMs = getTokenExpMs(accessToken);
  if (expMs === null) return;

  const msUntilRefresh = expMs - Date.now() - REFRESH_BEFORE_EXPIRY_MS;

  clearRefreshTimer();

  const performRefresh = async () => {
    if (!isOidcSession() || !localStorage.getItem(ACCESS_TOKEN)) {
      clearRefreshTimer();
      return;
    }
    const { data, error } = await refreshOidcToken();
    if (!isOidcSession() || !localStorage.getItem(ACCESS_TOKEN)) {
      clearRefreshTimer();
      return;
    }

    if (error?.response?.status === 409) {
      clearRefreshTimer();
      return;
    }

    if (error || !data?.access_token) {
      redirectToWelcomeBack();
      return;
    }

    setAccessToken(data.access_token);
    scheduleNextRefresh();
  };

  const handleUnexpectedError = () => {
    if (localStorage.getItem(ACCESS_TOKEN)) {
      redirectToWelcomeBack();
    }
  };

  if (msUntilRefresh <= 0) {
    performRefresh().catch(handleUnexpectedError);
    return;
  }

  refreshTimer = setTimeout(() => {
    performRefresh().catch(handleUnexpectedError);
  }, msUntilRefresh);
};

const onVisibilityChange = () => {
  if (paused) return;

  if (document.visibilityState === 'visible') {
    if (!localStorage.getItem(ACCESS_TOKEN)) {
      redirectToWelcomeBack();
      return;
    }
    scheduleNextRefresh();
  } else {
    clearRefreshTimer();
  }
};

const onStorageChange = (event: StorageEvent) => {
  if (event.key !== ACCESS_TOKEN) return;

  if (!event.newValue) {
    redirectToWelcomeBack();
  } else {
    scheduleNextRefresh();
  }
};

export const scheduleOidcRefresh = (): void => {
  paused = false;

  if (document.visibilityState === 'visible') {
    scheduleNextRefresh();
  }

  if (!listening) {
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('storage', onStorageChange);
    listening = true;
  }
};

export const pauseOidcRefresh = (): void => {
  paused = true;
  clearRefreshTimer();
};

export const resumeOidcRefresh = (): void => {
  paused = false;
  scheduleNextRefresh();
};

export const cancelOidcRefresh = (): void => {
  paused = false;
  clearRefreshTimer();

  if (listening) {
    document.removeEventListener('visibilitychange', onVisibilityChange);
    window.removeEventListener('storage', onStorageChange);
    listening = false;
  }

  stopSmCookieRefresh();
};
